/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Vincent Tran
 * Created: 08/02/23
 */

import { WithCurrentAssignmentProps } from '@/components/contexts/AssignmentContext/withCurrentAssignment';
import { Dialog } from '@/components/Dialog';
import { Workplan } from '@/wpt-lib/interfaces/Workplan';
import { useDeleteRequest } from '@/utils/useStatefulRequests';
import { Text } from '@chakra-ui/react';
import * as React from 'react';
import { useCallback, useLayoutEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const DeleteWorkplanDialog = ({
    workplan,
    onClose,
    currentAssignment,
}: {
    workplan?: Workplan;
    onClose(): void;
} & WithCurrentAssignmentProps) => {
    const [deleteErrorStr, setDeleteErrorStr] = useState<string>();
    const navigate = useNavigate();
    const { loading, makeRequest } = useDeleteRequest<Workplan>();
    const doDelete = useCallback(async () => {
        if (workplan) {
            try {
                await makeRequest(`/workplan/${workplan?.id}`, {
                    params: {
                        assignment: currentAssignment.id,
                    },
                });

                /* If already on the dashboard loading page, refresh a page to recreate
                 * current year workplan. */
                if (window.location.pathname === '/') window.location.reload();
                else navigate(`/`, {});

                onClose();
            } catch (e) {
                setDeleteErrorStr(`Could not delete ${workplan?.year} Workplan`);
            }
        }
    }, [makeRequest, workplan]);

    // reset on open
    useLayoutEffect(() => {
        if (workplan) {
            setDeleteErrorStr(undefined);
        }
    }, [workplan]);

    return (
        <Dialog
            isOpen={!!workplan}
            isLoading={loading}
            dialogHeader={`Delete ${workplan?.year} Draft Workplan`}
            dialogConfirmLabel="Delete"
            onClose={onClose}
            errorMsg={deleteErrorStr}
            dialogAction={doDelete}
        >
            <Text>
                Are you sure you want to delete this draft workplan? Once deleted the contents cannot be recovered.
            </Text>
        </Dialog>
    );
};

export default DeleteWorkplanDialog;
